import { Grid2, TextField, Tooltip, Typography } from "@mui/material";
import HelpOutlineSharpIcon from "@mui/icons-material/HelpOutlineSharp";
import { useTranslation } from "react-i18next";
import { zoullooColors } from "../../../../../theme";
import {
	AvailabilityViewModel,
	TimeCommitmentViewModel
} from "../../../../../ViewModels/FreelancerOnboardingMyAvailabilityStepViewModel";
import TimeCommitmentButton from "./TimeCommitmentButton";

interface TimeCommitmentSelectorProps {
	readonly selectedTimeCommitment: TimeCommitmentViewModel | null;
	readonly selectedAvailability: AvailabilityViewModel | null;
	readonly onTimeCommitmentSelect: (timeCommitment: TimeCommitmentViewModel) => void;
	readonly daysPerWeek: number | null;
	readonly onDaysPerWeekChange: (days: number) => void;
	readonly touched: boolean;
	readonly error: string | undefined;
	readonly daysPerWeekTouched: boolean;
	readonly daysPerWeekError: string | undefined;
	readonly showDaysPerWeek: boolean;
}

export default function TimeCommitmentSelector({
	selectedTimeCommitment,
	selectedAvailability,
	onTimeCommitmentSelect,
	daysPerWeek,
	onDaysPerWeekChange,
	touched,
	error,
	daysPerWeekTouched,
	daysPerWeekError,
	showDaysPerWeek
}: TimeCommitmentSelectorProps) {
	const { t } = useTranslation();

	return (
		<>
			{/* Full Time Button */}
			<Grid2
				size={{ xs: 12, md: 2 }}
				sx={{
					display: selectedAvailability === "available" ? "block" : "none"
				}}
			>
				<TimeCommitmentButton
					type="fullTime"
					isSelected={selectedTimeCommitment === "fullTime"}
					onClick={() => onTimeCommitmentSelect("fullTime")}
					size={{ xs: 12, md: 12 }}
				/>
			</Grid2>

			{/* Part Time Button */}
			<Grid2
				size={{ xs: 12, md: 2 }}
				sx={{
					display: selectedAvailability === "available" ? "block" : "none"
				}}
			>
				<TimeCommitmentButton
					type="partTime"
					isSelected={selectedTimeCommitment === "partTime"}
					onClick={() => onTimeCommitmentSelect("partTime")}
					size={{ xs: 12, md: 12 }}
				/>
			</Grid2>

			{selectedAvailability === "available" &&
				touched &&
				error &&
				!selectedTimeCommitment && (
					<Grid2 size={{ xs: 12 }}>
						<Typography color="error" variant="body1">
							{error}
						</Typography>
					</Grid2>
				)}

			{/* Days per week input - Only show when "partTime" is selected */}
			{showDaysPerWeek && (
				<Grid2
					size={{ xs: 12, md: 2 }}
					sx={{
						display: selectedAvailability === "available" ? "block" : "none"
					}}
				>
					<TextField
						label={
							<>
								{t("freelancerOnboardingMyAvailability.daysPerWeek")}
								<Tooltip
									title={
										<Typography variant="body2">
											{t("freelancerOnboardingMyAvailability.daysPerWeekTooltip")}
										</Typography>
									}
									arrow
									slotProps={{
										tooltip: {
											sx: { maxWidth: "500px !important" }
										}
									}}
								>
									<HelpOutlineSharpIcon
										fontSize="medium"
										sx={{
											ml: 1,
											mb: 0.5,
											color: zoullooColors.grey[400],
											verticalAlign: "middle",
											cursor: "help"
										}}
									/>
								</Tooltip>
							</>
						}
						type="number"
						value={daysPerWeek}
						onChange={e => {
							const newValue = Math.max(
								1,
								Math.min(5, Number.parseInt(e.target.value) || 1)
							);
							onDaysPerWeekChange(newValue);
						}}
						size="medium"
						fullWidth
						slotProps={{
							input: {
								inputProps: {
									min: 1,
									max: 5,
									style: {
										textAlign: "center",
										height: "17px"
									}
								}
							},
							htmlInput: {
								style: {
									MozAppearance: "textfield"
								}
							}
						}}
						sx={{
							"& .MuiOutlinedInput-root": {
								"& fieldset": {
									borderColor: zoullooColors.grey[400]
								},
								"&:hover fieldset": {
									borderColor: zoullooColors.grey[700]
								},
								"&.Mui-focused fieldset": {
									borderColor: zoullooColors.secondary?.main || "#6366f1"
								}
							},
							"& input[type=number]": {
								WebkitAppearance: "auto",
								MozAppearance: "textfield"
							},
							"& input[type=number]::-webkit-outer-spin-button": {
								WebkitAppearance: "auto",
								opacity: 1
							},
							"& input[type=number]::-webkit-inner-spin-button": {
								WebkitAppearance: "auto",
								opacity: 1
							}
						}}
					/>
				</Grid2>
			)}

			{selectedAvailability === "available" && daysPerWeekTouched && daysPerWeekError && (
				<Grid2 size={{ xs: 12 }}>
					<Typography color="error" variant="body1">
						{daysPerWeekError}
					</Typography>
				</Grid2>
			)}
		</>
	);
}
